import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'

import axios from 'axios'
import mixpanel from 'mixpanel-browser'

import {
  getQualityObservationType,
  listQualityObservationTitles,
  Observation,
  ObservationStatus,
  ObservationType,
  retrieveObservation,
  updateObservation,
} from '../../../../api/observations'
import { useBuildingContext } from '../../../../contexts/buildingContext'
import { useNotifications } from '../../../../contexts/notificationProvider'
import { useTagContext } from '../../../../contexts/tagContext'
import { useUserContext } from '../../../../contexts/userContext'

import {
  DetailsButton,
  FieldBlock,
  NotesTextarea,
  ObservationContainer,
  ObservationForm,
  ObservationHeader,
  StatusChip,
  TitleInput,
  TitleInputWrap,
  TitleRow,
  TitleSuggestionItem,
  TitleSuggestions,
} from './styles'

const BASE_URL = process.env.REACT_APP_SERVICES_API

interface QualityObservationInputProps {
  isNew: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- hydrate callback from TagManager
  hydrate: any
  observation?: Observation | null
  observationId?: number | string | null
}

const QualityObservationInput = ({
  isNew,
  hydrate,
  observation,
  observationId,
}: QualityObservationInputProps) => {
  const { state: userState } = useUserContext()
  const { state: buildingState } = useBuildingContext()
  const { findSetCurrentTag, state: tagState } = useTagContext()
  const { addNotification } = useNotifications()

  const [qualityType, setQualityType] = useState<ObservationType | null>(null)
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<ObservationStatus>('open')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)
  const suggestTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    getQualityObservationType().then(setQualityType)
  }, [])

  useEffect(() => {
    if (observation) {
      setTitle(observation.title ?? '')
      setNotes(observation.notes ?? '')
      setStatus(observation.status === 'closed' ? 'closed' : 'open')
    }
  }, [observation])

  const fetchSuggestions = useCallback(
    (q: string) => {
      if (suggestTimer.current) clearTimeout(suggestTimer.current)
      suggestTimer.current = setTimeout(() => {
        if (!buildingState.projectId) {
          setSuggestions([])
          return
        }
        listQualityObservationTitles(buildingState.projectId, q.trim())
          .then(titles => setSuggestions(titles))
          .catch(() => setSuggestions([]))
      }, 200)
    },
    [buildingState.projectId],
  )

  const onTitleChange = (value: string) => {
    setTitle(value)
    setShowSuggestions(true)
    fetchSuggestions(value)
  }

  const toggleStatus = async () => {
    const next: ObservationStatus = status === 'open' ? 'closed' : 'open'
    setStatus(next)
    if (isNew || !observationId) return
    try {
      await updateObservation(buildingState.projectId, String(observationId), {
        status: next,
      })
      addNotification(
        next === 'closed' ? 'Marked closed' : 'Marked open',
        'success',
      )
      hydrate()
    } catch {
      setStatus(status)
      addNotification('Could not update status', 'error')
    }
  }

  const createNewTag = async () => {
    if (!qualityType) {
      addNotification('Quality observation type is not configured', 'error')
      return
    }
    if (!title.trim()) {
      addNotification('Title is required', 'warning')
      return
    }
    setSaving(true)
    try {
      const observationData = {
        project: buildingState.projectId,
        type: qualityType.id,
        title: title.trim(),
        notes: notes.trim() || null,
        status: 'open' as ObservationStatus,
        unsafe: false,
        severity: 'SEVERITY_LOW',
        vp_image: buildingState.imageData.data.id,
        claimed: true,
        verified: true,
        is_manual: true,
        project_floor: buildingState.floorData.id,
        ...((): { processed_image?: number } => {
          const pi = buildingState.imageData.processed_image
          const id =
            pi && typeof pi === 'object' ? (pi as { id?: number }).id : pi
          return typeof id === 'number' ? { processed_image: id } : {}
        })(),
      }
      const res = await axios.post(`${BASE_URL}/tags/`, {
        creator: userState.public_id,
        pitch: tagState.tags.current.pitch,
        yaw: tagState.tags.current.yaw,
        project_id: buildingState.projectId,
        point_id: buildingState.pointId,
        floor_id: buildingState.floorId,
        image_id: buildingState.imageData.data.id,
        type: 'QUALITY',
        observation_data: observationData,
        image_data: {
          width: buildingState.imageData.dimensions.width,
          height: buildingState.imageData.dimensions.height,
          processed_image: buildingState.imageData.processed_image,
        },
      })
      await hydrate()
      findSetCurrentTag(res.data.id)
      addNotification('Quality observation saved', 'success')
      mixpanel.track('Create Quality Tag')
    } catch {
      addNotification('Failed to save quality observation', 'error')
    } finally {
      setSaving(false)
    }
  }

  const saveExisting = async () => {
    if (!observationId) return
    if (!title.trim()) {
      addNotification('Title is required', 'warning')
      return
    }
    setSaving(true)
    try {
      await updateObservation(buildingState.projectId, String(observationId), {
        title: title.trim(),
        notes: notes.trim() || null,
        status,
      })
      addNotification('Quality observation updated', 'success')
      mixpanel.track('Update Quality Tag')
      hydrate()
    } catch {
      addNotification('Failed to update quality observation', 'error')
    } finally {
      setSaving(false)
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (isNew) {
      void createNewTag()
    } else {
      void saveExisting()
    }
  }

  return (
    <ObservationForm onSubmit={onSubmit} className="pnlm-ignore oco-dialogue">
      <FieldBlock>
        <TitleRow>
          <label htmlFor="quality-observation-title">Title</label>
          {!isNew && (
            <StatusChip $status={status} onClick={() => void toggleStatus()}>
              {status === 'open' ? 'Open' : 'Closed'}
            </StatusChip>
          )}
        </TitleRow>
        <TitleInputWrap>
          <TitleInput
            id="quality-observation-title"
            className="pnlm-ignore oco-dialogue"
            value={title}
            onChange={e => onTitleChange(e.target.value)}
            onFocus={() => {
              setShowSuggestions(true)
              fetchSuggestions(title)
            }}
            onBlur={() => {
              // Allow click on suggestion before closing.
              setTimeout(() => setShowSuggestions(false), 150)
            }}
            required
          />
          {showSuggestions && suggestions.length > 0 && (
            <TitleSuggestions className="pnlm-ignore oco-dialogue">
              {suggestions.map(s => (
                <TitleSuggestionItem
                  key={s}
                  className="pnlm-ignore oco-dialogue"
                  onMouseDown={e => {
                    e.preventDefault()
                    setTitle(s)
                    setShowSuggestions(false)
                  }}
                >
                  {s}
                </TitleSuggestionItem>
              ))}
            </TitleSuggestions>
          )}
        </TitleInputWrap>
      </FieldBlock>
      <FieldBlock>
        <label htmlFor="quality-observation-notes">Notes</label>
        <NotesTextarea
          id="quality-observation-notes"
          className="pnlm-ignore oco-dialogue"
          rows={4}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </FieldBlock>
      <DetailsButton disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </DetailsButton>
    </ObservationForm>
  )
}

interface QualityTagProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tag from tagContext
  qualityTag: any
  transform: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- hydrate callback
  hydrate: any
}

export const QualityTag = ({
  qualityTag,
  transform,
  hydrate,
}: QualityTagProps) => {
  const { state: buildingState } = useBuildingContext()
  const [observation, setObservation] = useState<Observation | null>(null)

  useEffect(() => {
    if (qualityTag?.observation) {
      retrieveObservation(buildingState.projectId, qualityTag.observation).then(
        d => setObservation(d.data),
      )
      mixpanel.track('View Quality Tag')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when tag changes
  }, [qualityTag])

  return (
    <ObservationContainer
      style={{ transform }}
      id="tag-container"
      className="down-arrow pnlm-ignore oco-dialogue"
    >
      <ObservationHeader>
        <h2>Quality Observation</h2>
      </ObservationHeader>
      <QualityObservationInput
        isNew={!qualityTag.observation}
        observation={observation}
        observationId={qualityTag.observation}
        hydrate={hydrate}
      />
    </ObservationContainer>
  )
}
